import React, { useEffect, useMemo } from 'react'

import { useDropdownState } from '@sketch/components'
import {
  InspectorLayersTreeNode,
  useInspectorContext,
} from '../../hooks/inspector'

import { useSubLayersTreeAtRelativePosition } from './useSubLayersTreeAtRelativePosition'
import { LayerIcon } from './LayerIcon'

import * as S from './ArtboardDetailInspectorContextMenu.styles'
import { isArtboard } from '../../hooks/inspector/sketchSceneLib/inspectorTreeNodeUtilsDeprecated'

type ArtboardDetailInspectorContextMenuProps = {
  className?: string
  rootNode?: InspectorLayersTreeNode
  visible?: boolean
  /**
   * Menu position (relative to the artboard element)
   */
  contextMenuPosition: {
    x: number
    y: number
  } | null
  onCloseMenu: () => void
}

/**
 * The context menu opening when right clicking on the artboard canvas when
 * using the inspector. This shows a tree of all the layers under the cursor.
 */
export function ArtboardDetailInspectorContextMenu({
  className,
  contextMenuPosition,
  onCloseMenu,
}: ArtboardDetailInspectorContextMenuProps) {
  const { selectedNode, setSelectedLayerNode } = useInspectorContext()
  const layersTreeRootNode = useSubLayersTreeAtRelativePosition(
    contextMenuPosition
  )
  const dropdown = useDropdownState({
    onToggle: onCloseMenu,
  })

  const layersTreeNodeList = useMemo(() => {
    if (!layersTreeRootNode) {
      return []
    }

    // We don't want to show the artboard in the context menu.
    return isArtboard(layersTreeRootNode)
      ? layersTreeRootNode.children
      : [layersTreeRootNode]
  }, [layersTreeRootNode])

  useEffect(() => {
    if (layersTreeNodeList.length === 0 && contextMenuPosition) {
      // If the user right clicked to open menu but we realize that there is nothing
      // to show where the user clicked, reset the "open" position by closing the menu.
      onCloseMenu()
    }
  }, [contextMenuPosition, layersTreeNodeList.length, onCloseMenu])

  if (
    !contextMenuPosition ||
    !layersTreeRootNode ||
    // We are already closing the menu in the use effect above but just in case,
    // let's avoid render the menu at all.
    layersTreeNodeList.length === 0
  ) {
    return null
  }

  return (
    <S.DropdownWrapper
      {...dropdown}
      onClickOutside={dropdown.toggleDropdown}
      placement="bottom-start"
      style={{
        left: contextMenuPosition.x,
        top: contextMenuPosition.y,
      }}
      contentStyle={{ maxWidth: 500 }}
      className={className}
      visible={true} // through the visible flag
      maxWidth="100%"
    >
      <S.ContextMenuNav>
        <ContextMenuList
          layersTreeNodeList={layersTreeNodeList}
          level={0}
          selectedLayerUuid={selectedNode?.nodeUUID}
          onSelect={(layerNode: InspectorLayersTreeNode) => {
            setSelectedLayerNode(layerNode)
            onCloseMenu()
          }}
        />
      </S.ContextMenuNav>
    </S.DropdownWrapper>
  )
}

type ContextMenuListProps = {
  layersTreeNodeList: InspectorLayersTreeNode[]
  level: number
  selectedLayerUuid: string | undefined
  onSelect: (layerNode: InspectorLayersTreeNode) => void
}
/**
 * A recursive component that renders a layer tree into ordered lists
 */
function ContextMenuList({
  layersTreeNodeList,
  level,
  selectedLayerUuid,
  onSelect,
}: ContextMenuListProps) {
  return (
    <ol key={`level${level}`}>
      {layersTreeNodeList.map(layersTreeNode => {
        const isLayerMasked = !isArtboard(layersTreeNode)
          ? layersTreeNode.isLayerMasked
          : false

        return (
          <li
            key={layersTreeNode.identifier.toString()}
            onClick={e => {
              // Make sure the click doesn't propagate to the parent
              // since this component is rendered recursively.
              e.stopPropagation()
              onSelect(layersTreeNode)
            }}
          >
            <S.ContextMenuLabel
              level={level}
              isSelected={selectedLayerUuid === layersTreeNode.nodeUUID}
            >
              <LayerIcon
                layerType={layersTreeNode.type}
                isLayerMasked={isLayerMasked}
                isLayerExportable={layersTreeNode.isExportable}
              />
              <p>{layersTreeNode.layerName}</p>
            </S.ContextMenuLabel>
            <ContextMenuList
              layersTreeNodeList={layersTreeNode.children}
              level={level + 1}
              selectedLayerUuid={selectedLayerUuid}
              onSelect={onSelect}
            />
          </li>
        )
      })}
    </ol>
  )
}
